import '@mantine/core/styles.css';
import '@mantine/nprogress/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/dates/styles.css';
import '@/styles/globals.css';
import '@/styles/voice-line.css';
import '@/styles/atlas.css';

import React from 'react';

import { initConsoleGuard } from '@monorepo/utils';

import { partnerClickCandidate } from '@/utils/partner-click';

// Silence browser console on silenced environments (staging / production).
// Controlled by NEXT_PUBLIC_ENABLE_CONSOLE_LOGS; per-developer opt-in via
// localStorage. Runs as a module-level side effect so it takes effect before
// any React render. See docs/console-guard.md for details.
initConsoleGuard();
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { DefaultSeo } from 'next-seo';

import CookieConsentBanner, {
  CookieConsentProvider,
  GoogleTag,
} from '@/components/CookieConsentBanner';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AtlasRouteDraftBoundary } from '@/components/modules/Atlas/expert/AtlasRouteDraftBoundary';
import RouterTransition from '@/components/RouterTransition';
import theme from '@/styles/theme';
import { isCustomerChatSurface } from '@/utils/customerChatSurface';
import { disableGoogleTag, isGoogleTagInitialized } from '@/utils/customerChatTagGuard';
import { GOOGLE_ADS_ID, GOOGLE_ANALYTICS_ID } from '@/utils/googleAds';

import type { NextPageWithLayout } from '../types';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const DEFAULT_SEO = {
  titleTemplate: '%s | Chat.co',
  defaultTitle: 'Chat.co - AI-Powered Knowledge Agents with Cited Answers',
  description:
    'Transform fragmented organizational knowledge into intelligent AI agents. Chat.co delivers answers with source citations for review. Multi-LLM support: OpenAI, Claude, Gemini.',
  // canonical and openGraph.url are set per-page in <App> below — a static
  // value here would claim every page is that one URL.
  openGraph: {
    type: 'website',
    locale: 'en_US',
    siteName: 'Chat.co',
    title: 'Chat.co - AI-Powered Knowledge Agents',
    description:
      'Transform fragmented organizational knowledge into intelligent AI agents. Get verified answers with source citations.',
    images: [
      {
        url: 'https://chat.co/images/og-image.png',
        width: 1200,
        height: 630,
        alt: 'Chat.co AI Knowledge Platform',
      },
    ],
  },
  twitter: {
    handle: '@chatdotco',
    site: '@chatdotco',
    cardType: 'summary_large_image',
  },
  additionalMetaTags: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1',
    },
    {
      name: 'theme-color',
      content: '#004790',
    },
    {
      name: 'robots',
      content: 'index, follow',
    },
  ],
};

const PRIMARY_APP_HOSTS = new Set<string>([
  'localhost',
  '127.0.0.1',
  'staging.sa-cyber.com',
  'chat.sa-cyber.com',
  'sa-cyber.com',
  'www.sa-cyber.com',
  'chat.co',
  'www.chat.co',
  'chatdemo.co',
  'web.chatdemo.co',
  'staging.chatdemo.co',
]);

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  React.useEffect(() => { partnerClickCandidate(); }, [router.asPath]);

  // og:url and canonical must describe THIS page. DEFAULT_SEO pinned both to
  // the site root, so every page that didn't set its own — the marketplace,
  // use-cases/*, schools, privacy/terms/faqs — told scrapers it *was* the
  // homepage. Derive them from the current route instead; a page's own
  // <NextSeo> canonical / openGraph.url still overrides this.
  //
  // Auto-statically-optimized dynamic routes (c/[pk], share/[token]) prerender
  // with the route *pattern* as asPath, and crawlers read that HTML without
  // running the client router — so rather than emit a bracketed template URL,
  // emit no og:url/canonical at all and let scrapers fall back to the URL they
  // actually fetched, which is always correct.
  const routePath = (router.asPath || '/').split(/[?#]/)[0];
  const pageUrl = routePath.includes('[')
    ? undefined
    : `https://chat.co${routePath === '/' ? '' : routePath}`;

  // The embedded widget and hosted chat UI serve a customer's visitors, often
  // inside an iframe on the customer's own site: no chat.co cookie banner or
  // Google tag there, and no consent cookie handling. router.pathname is the
  // route pattern, so this holds during SSR and prerender too. The browser path
  // covers a customer chat URL that renders the 404/error page (pathname
  // '/404' or '/_error'); the banner and tag only ever mount on the client, so
  // the server's '/404' answer for that URL never reaches the page.
  const customerChatSurface =
    isCustomerChatSurface(router.pathname) || isCustomerChatSurface(routePath);

  // Last resort: the customer-chat tag guard (installed with GoogleTag) turns
  // navigations into a customer chat page into full page loads, so this should
  // never fire. If one is ever rendered in a document where gtag.js already
  // runs, reload so it runs without it. Runs after render, so it is cleanup,
  // not prevention.
  React.useEffect(() => {
    if (customerChatSurface && isGoogleTagInitialized()) {
      disableGoogleTag([GOOGLE_ANALYTICS_ID, GOOGLE_ADS_ID]);
      window.location.reload();
    }
  }, [customerChatSurface]);

  // Create QueryClient outside of useState to avoid SSR issues
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
          },
        },
      })
  );

  // Get the page's layout function, or use default (no wrapper)
  const getLayout = Component.getLayout ?? ((page) => page);

  // Recovery for stale builds & transient navigation errors.
  // After a redeployment the old client holds a stale buildId; any
  // client-side navigation will 404 on /_next/data/{old-build}/{page}.json
  // and fire routeChangeError.  A full page reload fetches the new build.
  // To avoid infinite reload loops we only reload once per URL per session.
  React.useEffect(() => {
    const handleRouteError = (err: any, url: string) => {
      if (err.cancelled) return;

      console.error('[Navigation] routeChangeError:', err.message || err, '→', url);

      try {
        const key = `nav-reload:${url}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.href = url;
        }
      } catch {
        // sessionStorage blocked – ignore
      }
    };

    router.events.on('routeChangeError', handleRouteError);
    return () => {
      router.events.off('routeChangeError', handleRouteError);
    };
  }, [router]);

  // Client-side guard: on customer custom domains, only allow embed routes.
  // Redirect all other routes back to the canonical app domain so customers
  // cannot host the full app UI under their own domain.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const { hostname } = window.location;
    const path = window.location.pathname || '';

    const isPrimaryHost =
      PRIMARY_APP_HOSTS.has(hostname) ||
      (process.env.NODE_ENV === 'development' && hostname.endsWith('.localhost')) ||
      hostname.endsWith('.vercel.app') ||
      hostname.endsWith('.vercel.sh');

    const isEmbedRoute = path === '/embed' || path.startsWith('/embed/');

    if (!isPrimaryHost && !isEmbedRoute) {
      const canonical = new URL(window.location.href);
      canonical.protocol = 'https:';
      canonical.hostname = 'chat.co';
      canonical.port = '';

      window.location.replace(canonical.toString());
    }
  }, [router.asPath]);

  // Ensure CSS variable theme is applied globally (including marketing pages that don't use SidebarProvider)
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedTheme = localStorage.getItem('sidebar-theme');
      let themeToApply: 'dark' | 'light';
      if (storedTheme === 'dark' || storedTheme === 'light') {
        themeToApply = storedTheme;
      } else {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        themeToApply = prefersDark ? 'dark' : 'light';
      }
      document.documentElement.setAttribute('data-theme', themeToApply);
    } catch {
      // Ignore storage access issues
    }
  }, []);

  // MSW setup - only use on client side when explicitly enabled
  // Initialize MSW only on client side, skip completely during SSR
  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_API_MOCKING === 'true') {
      import('../apis/mocks/browser')
        .then(async ({ worker }) => {
          await worker.start();
        })
        .catch((err) => {
          console.error('MSW initialization failed:', err);
        });
    }

    // Global error handler for uncaught form-related errors
    const handleError = (event: ErrorEvent) => {
      if (
        event.error?.message?.includes('destructure') ||
        event.error?.message?.includes('register') ||
        event.error?.message?.includes('watch')
      ) {
        console.error('🔥 UNCAUGHT FORM ERROR:', {
          message: event.error.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error.stack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        });
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        event.reason?.message?.includes('destructure') ||
        event.reason?.message?.includes('register') ||
        event.reason?.message?.includes('watch')
      ) {
        console.error('🔥 UNCAUGHT FORM PROMISE REJECTION:', {
          reason: event.reason,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        });
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // CRITICAL: Providers must wrap the result of getLayout, not be inside it
  // This ensures MantineProvider is always present for all pages during SSG
  return (
    <>
      <DefaultSeo
        {...DEFAULT_SEO}
        canonical={pageUrl}
        openGraph={{ ...DEFAULT_SEO.openGraph, url: pageUrl }}
      />
      <ErrorBoundary>
        <CookieConsentProvider disabled={customerChatSurface}>
          {!customerChatSurface && routePath !== '/auth/connector-reviewer' && (
            <GoogleTag gaId={GOOGLE_ANALYTICS_ID} adsId={GOOGLE_ADS_ID} />
          )}
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={pageProps.dehydratedState}>
              <MantineProvider theme={theme} defaultColorScheme="light">
                <RouterTransition />
                <Notifications />
                {process.env.NODE_ENV === 'development' && (
                  <ReactQueryDevtools initialIsOpen={false} />
                )}

                {/* One instance for the whole App (no key): unsent Atlas saved-chat text can survive a page change
                    out of Atlas and back during the same sign-in (memory only). */}
                <AtlasRouteDraftBoundary queryClient={queryClient} path={routePath}>
                  {getLayout(<Component {...pageProps} />)}
                </AtlasRouteDraftBoundary>
                {!customerChatSurface && <CookieConsentBanner />}
              </MantineProvider>
            </HydrationBoundary>
          </QueryClientProvider>
        </CookieConsentProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
